import { createContext, useContext, useEffect, useRef, useState } from "react";
import { clsx } from "clsx";
import styles from "./Pagination.module.css";

interface PaginationContextValue {
	page: number;
	pageCount: number;
	disabled: boolean;
	getPageLabel: (page: number) => string;
	setPage: (page: number) => void;
}

const PaginationContext = createContext<PaginationContextValue | null>(null);

function usePaginationContext(part: string): PaginationContextValue {
	const context = useContext(PaginationContext);
	if (context === null) {
		throw new Error(`<Pagination.${part}> must be rendered inside <Pagination.Root>.`);
	}
	return context;
}

const clampPage = (page: number, pageCount: number) => Math.min(Math.max(page, 1), Math.max(pageCount, 1));

const defaultGetPageLabel = (page: number) => String(page);

/**
 * native button props minus the ones the primitive controls.
 * Hyphenated attributes (aria-*, data-*) cannot be excluded this way — JSX always
 * permits them — so the parts spread `props` first and win by attribute order.
 */
type PaginationButtonProps = Omit<React.ComponentProps<"button">, "type" | "disabled" | "onClick" | "tabIndex">;

/**
 * Pagination component. NOT based on BaseUI
 * Styled like the .paginate_button from datatable.less
 */
function PaginationRoot({
	page: controlledPage,
	defaultPage = 1,
	pageCount,
	onPageChange,
	disabled = false,
	getPageLabel = defaultGetPageLabel,
	className,
	children,
	"aria-label": ariaLabel = "Pagination",
	onKeyDown,
	...props
}: Omit<React.ComponentProps<"nav">, "onChange"> & {
	/** current page (when controlled), 1-based and clamped into 1..pageCount */
	page?: number;
	defaultPage?: number;
	/** number of pages; 0 renders an empty bar with both arrows disabled */
	pageCount: number;
	onPageChange?: (page: number) => void;
	disabled?: boolean;
	/** accessible label of a page button */
	getPageLabel?: (page: number) => string;
}) {
	const [uncontrolledPage, setUncontrolledPage] = useState(defaultPage);
	const page = clampPage(controlledPage ?? uncontrolledPage, pageCount);

	const navRef = useRef<HTMLElement>(null);
	const lastDirection = useRef<1 | -1>(1);
	const focusCurrentItem = useRef(false);

	const setPage = (next: number) => {
		const clamped = clampPage(next, pageCount);
		if (clamped === page) return;
		if (controlledPage === undefined) setUncontrolledPage(clamped);
		onPageChange?.(clamped);
	};

	// arrow navigation changes the page, so focus has to follow the button that
	// becomes the current page on the next render
	useEffect(() => {
		if (!focusCurrentItem.current) return;
		focusCurrentItem.current = false;
		navRef.current?.querySelector<HTMLButtonElement>('[aria-current="page"]')?.focus();
	}, [page]);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
		onKeyDown?.(event);
		if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
		if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
			event.preventDefault();
			const direction = event.key === "ArrowRight" ? 1 : -1;
			lastDirection.current = direction;
			if (clampPage(page + direction, pageCount) === page) return;
			focusCurrentItem.current = true;
			setPage(page + direction);
		} else if (event.key === "Tab" && !event.shiftKey) {
			const onItem = event.target instanceof HTMLElement && event.target.dataset.slot === "pagination-item";
			if (!onItem) return;
			const boundary = lastDirection.current === 1 ? pageCount : 1;
			const target = page === boundary ? (boundary === 1 ? pageCount : 1) : boundary;
			if (target === page) return;
			event.preventDefault();
			focusCurrentItem.current = true;
			setPage(target);
		} else if (event.key === "Escape") {
			if (event.target instanceof HTMLElement) event.target.blur();
		}
	};

	const value: PaginationContextValue = {
		page,
		pageCount,
		disabled,
		getPageLabel,
		setPage,
	};

	return (
		<PaginationContext.Provider value={value}>
			<nav
				{...props}
				ref={navRef}
				data-slot="pagination"
				aria-label={ariaLabel}
				className={className}
				onKeyDown={handleKeyDown}
			>
				<ul className={styles.list}>{children}</ul>
			</nav>
		</PaginationContext.Provider>
	);
}

function PaginationPrevious({ className, children, ...props }: PaginationButtonProps) {
	const { page, disabled, setPage } = usePaginationContext("Previous");
	return (
		<li>
			<button
				{...props}
				type="button"
				data-slot="pagination-previous"
				className={clsx(styles.item, className)}
				disabled={disabled || page <= 1}
				tabIndex={-1}
				onClick={() => setPage(page - 1)}
			>
				{children ?? "Previous"}
			</button>
		</li>
	);
}

function PaginationNext({ className, children, ...props }: PaginationButtonProps) {
	const { page, pageCount, disabled, setPage } = usePaginationContext("Next");
	return (
		<li>
			<button
				{...props}
				type="button"
				data-slot="pagination-next"
				className={clsx(styles.item, className)}
				disabled={disabled || page >= pageCount}
				tabIndex={-1}
				onClick={() => setPage(page + 1)}
			>
				{children ?? "Next"}
			</button>
		</li>
	);
}

/** One page-number button; `Items` renders these, use directly only in custom compositions. */
function PaginationItem({ page: itemPage, className, children, ...props }: PaginationButtonProps & { page: number }) {
	const { page, disabled, getPageLabel, setPage } = usePaginationContext("Item");
	const current = itemPage === page;
	return (
		<li>
			<button
				{...props}
				type="button"
				data-slot="pagination-item"
				className={clsx(styles.item, className)}
				disabled={disabled}
				aria-label={getPageLabel(itemPage)}
				aria-current={current ? "page" : undefined}
				data-selected={current || undefined}
				tabIndex={current ? 0 : -1}
				onClick={() => setPage(itemPage)}
			>
				{children ?? itemPage}
			</button>
		</li>
	);
}

/*
 * calculates which page numbers are currently shown and which are omitted
 */
function pageItems(page: number, pageCount: number, siblingCount: number): (number | "start-ellipsis" | "end-ellipsis")[] {
	const range = (from: number, to: number) => Array.from({ length: to - from + 1 }, (_, index) => from + index);

	if (pageCount <= 2 * siblingCount + 5) return range(1, pageCount);

	const start = Math.max(Math.min(page - siblingCount, pageCount - 2 * siblingCount - 2), 3);
	const end = Math.min(Math.max(page + siblingCount, 2 * siblingCount + 3), pageCount - 2);
	return [
		1,
		start > 3 ? ("start-ellipsis" as const) : 2,
		...range(start, end),
		end < pageCount - 2 ? ("end-ellipsis" as const) : pageCount - 1,
		pageCount,
	];
}

/** The numbered page buttons with ellipsis truncation. */
function PaginationItems({ siblingCount = 1 }: { siblingCount?: number }) {
	const { page, pageCount } = usePaginationContext("Items");
	return pageItems(page, pageCount, siblingCount).map((item) =>
		typeof item === "number" ? (
			<PaginationItem key={item} page={item} />
		) : (
			<li key={item}>
				<span data-slot="pagination-ellipsis" className={styles.ellipsis} aria-hidden="true">
					…
				</span>
			</li>
		),
	);
}

const Pagination = {
	Root: PaginationRoot,
	Previous: PaginationPrevious,
	Next: PaginationNext,
	Items: PaginationItems,
	Item: PaginationItem,
};

export { Pagination };
